import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { RegisterEntityModule } from 'core/constants';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { useParams, useLocation } from 'react-router-dom';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';

import Grid from 'components/Grid';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { formsListBasicColumns } from 'containers/Forms/components/FormsList/FormsList';

import { env } from 'config';

import { getRelatedEntityDetails } from '../helper';
import { entityAuditsPageColumns } from './utils';

const RelatedAudits: React.FunctionComponent = () => {
  const {
    routing,
    session: { user },
  } = useStore();
  const onRowClick: GridEventListener<'rowClick'> = (form: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.audits.forms.details, { formId: form.row.uuid }));
  };

  const navBarHeight = useNavBarHeight();

  const params = useParams<{ entityId: string }>();
  const location = useLocation();
  const { entityId } = params;

  const {
    store: { loadDetails, details, path, detailsPath },
    translations,
    nameKey,
    entityType,
  } = getRelatedEntityDetails(location.pathname);

  const auditsOnEntity = !!user?.auditsAsEntityEnabled;

  React.useEffect(() => {
    const fetchDetails = async (): Promise<void> => {
      loadDetails(entityId as string);
    };

    fetchDetails();
  }, [entityId]);

  return (
    <Record
      Header={
        <MainHeader
          title={`Related Audits to ${details?.entity[nameKey].value || ''}`}
          breadcrumbs={[
            { label: translations.plural, link: path },
            { label: 'Details', link: detailsPath(entityId as string) },
            {
              label: 'Related Audits',
              link: reverse(routes.dashboard[entityType as RegisterEntityModule].relatedAudits, {
                entityId: entityId as string,
              }),
            },
          ]}
        />
      }
      Content={(onError) => (
        <Grid
          // The entity endpoint orders newest-first server-side and supports neither free-text search
          // nor filtering, so those controls would silently ignore whatever is entered.
          sortBy={auditsOnEntity ? undefined : { field: 'submitted', sort: 'desc' }}
          showQuickFilter={!auditsOnEntity}
          disableColumnFilter={auditsOnEntity}
          disableSavePreferences
          basicColumns={auditsOnEntity ? entityAuditsPageColumns : formsListBasicColumns}
          entity={auditsOnEntity ? 'audits' : 'forms'}
          onError={onError}
          dataURL={
            auditsOnEntity
              ? `/api/entities/${entityType}/${entityId}/audits/`
              : `${env.REACT_APP_WORKFLOWS_API_URL}inspections/?entity=${entityId}`
          }
          disableColumnSelector
          onRowClick={onRowClick}
          customNoRowsText={auditsOnEntity ? 'No audits are related to this record' : 'No forms have been created yet'}
        />
      )}
      navBarHeight={navBarHeight}
    />
  );
};

export default observer(RelatedAudits);
